/**
 * marketing_flag_controller.js — one-tap flagging of funnel events (Stimulus).
 *
 * Serves the "Needs a human read" queue on the MI Today view. Each row carries
 * one small form per offerable signal; this controller intercepts the submit,
 * POSTs it in the background, and removes the row on success.
 *
 * Why intercept at all: the queue is a working list. A full page reload per
 * flag throws away scroll position and re-renders the chart, which turns
 * clearing twenty events into twenty page loads.
 *
 * Progressive enhancement, deliberately: the markup is real <form> elements
 * posting to the same admin route the channel-detail page uses. With this
 * controller absent or broken, every button still works as a normal POST and
 * lands back on Today (the route's return_to field). There is no second write
 * path — success and failure both go through MarketingService.flag_event.
 *
 * On failure the row is left in place and re-enabled: a flag that did not
 * persist must not look like one that did.
 *
 * Two behaviours, one controller, chosen by data-marketing-flag-keep-row-value:
 *
 *   false (default, Today) — the queue IS "things awaiting a read", so a
 *          flagged row has left the queue and is removed.
 *   true  (channel detail) — the list is "this channel's funnel events", which
 *          a flag annotates rather than removes; the row stays and is marked.
 */
import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
    static targets = ["row"];
    static values = { keepRow: Boolean };

    submit(event) {
        const form = event.target;
        // Let the browser handle it if fetch is unavailable, rather than
        // swallowing the submit and silently doing nothing.
        if (typeof window.fetch !== "function") return;

        event.preventDefault();
        const row = form.closest("[data-marketing-flag-target='row']");
        this._setBusy(row, true);

        fetch(form.action, {
            method: "POST",
            body: new FormData(form),
            headers: { "X-Requested-With": "XMLHttpRequest" },
            credentials: "same-origin",
            redirect: "follow",
        })
            .then((response) => {
                if (!response.ok) throw new Error(`HTTP ${response.status}`);
                if (this.keepRowValue) this._markFlagged(row);
                else this._remove(row);
            })
            .catch((error) => {
                console.error("[marketing-flag] flag failed", error);
                this._setBusy(row, false);
            });
    }

    _setBusy(row, busy) {
        if (!row) return;
        row.classList.toggle("opacity-50", busy);
        row.querySelectorAll("button").forEach((button) => {
            button.disabled = busy;
        });
    }

    _remove(row) {
        if (!row) return;
        row.remove();
        this._decrementBadge();
    }

    /**
     * Confirm the write without removing the row.
     *
     * The select already shows the chosen value, so the only thing missing is
     * evidence that it reached the server; without it a failed POST and a
     * successful one look identical.
     */
    _markFlagged(row) {
        this._setBusy(row, false);
        if (!row) return;
        row.classList.add("table-success");
        window.setTimeout(() => row.classList.remove("table-success"), 1200);
    }

    /**
     * Keep the header count honest as rows leave.
     *
     * The badge is the reason this queue exists — it counted events nobody
     * could clear — so a stale badge here would recreate the original
     * complaint in miniature.
     */
    _decrementBadge() {
        const badge = this.element.closest(".card")?.querySelector(".card-header .badge");
        if (!badge) return;
        const current = parseInt(badge.textContent.trim(), 10);
        if (Number.isNaN(current)) return;
        badge.textContent = String(Math.max(0, current - 1));
    }
}
